import { navigate } from '@reach/router';
import {
  LessonsApi,
  LessonsResult,
  Subject,
  SubjectContents,
} from 'api/lessonsApi';
import { Resource, ResourcesApi, ResourcesTileType } from 'api/resourcesApi';
import { AppPath } from 'appConstants';
import DocumentIcon from 'images/lessons/Img_Resource_Document.png';
import PresentationIcon from 'images/lessons/Full_Screen_Icon.png';
import AudioIcon from 'images/resources/Img_Resource_Audio.png';
import DefaultIcon from 'images/resources/Img_Resource_Default.png';
import VideoIcon from 'images/resources/Img_Resource_Video.png';
import ResourcesByActivityImage from 'images/resources/Resources_By_Activity.png';
import ResourcesByTopicImage from 'images/resources/Resources_By_Topic.png';
import { useEffect, useState } from 'react';

export type ResourceTypeTab = {
  resourceType: string;
  segment: string;
  icon: string;
  /** Each type keeps its own cell class, whichever tabs show */
  cellClass: string;
};

/** The resource type tabs, in the order they show and are picked */
const RESOURCE_TYPE_TABS: ResourceTypeTab[] = [
  {
    resourceType: 'Document',
    segment: 'document',
    icon: DocumentIcon,
    cellClass: 'Cell_KS_01',
  },
  {
    resourceType: 'Video',
    segment: 'video',
    icon: VideoIcon,
    cellClass: 'Cell_KS_02',
  },
  {
    resourceType: 'Audio',
    segment: 'audio',
    icon: AudioIcon,
    cellClass: 'Cell_KS_03',
  },
  {
    resourceType: 'Presentation',
    segment: 'presentation',
    icon: PresentationIcon,
    cellClass: 'Cell_KS_04',
  },
];

/** The order search results group resources by type */
export const SEARCH_RESOURCE_TYPES = [
  'Document',
  'Video',
  'Audio',
  'Image',
  'Presentation',
];

export const resourceTypeIcon = (resourceType: string) =>
  RESOURCE_TYPE_TABS.find((tab) => tab.resourceType === resourceType)?.icon ??
  DefaultIcon;

/**
 * The tabs for the types present, and the one open: the type asked for when present, else the
 * first
 * @param segment - The type segment of the page path, if any
 */
export const resourceTypeTabs = (
  resources: Resource[],
  segment: string | undefined,
  bWithPresentation: boolean
) => {
  const tabs = RESOURCE_TYPE_TABS.filter(
    (tab) =>
      (bWithPresentation || tab.resourceType !== 'Presentation') &&
      resources.some((resource) => resource.resourceType === tab.resourceType)
  );
  const activeTab =
    tabs.find((tab) => tab.segment === segment?.toLowerCase()) ??
    tabs[0] ??
    null;

  return { tabs, activeTab };
};

export const byName = <T extends { name: string }>(items: T[]) =>
  [...items].sort((a, b) => a.name.localeCompare(b.name));

/** The "Resources by Activity" tile's picture, fixed per subject */
export const resourcesByActivityImage = (subject: Subject) => {
  switch (subject.shortName) {
    case 'PE':
      return ResourcesByActivityImage;
    case 'Music':
      return ResourcesByTopicImage;
    default:
      return null;
  }
};

/** A tile or card's classes, then its colour class when it has one */
export const withColour = (
  className: string,
  colorCSS: string | null | undefined
) => (colorCSS ? `${className} ${colorCSS}` : className);

export const resourceBankPath = (subjectCode: string) =>
  `/${subjectCode.toLowerCase()}/resourcebank`;

export const awesomeStuffPath = (subjectCode: string) =>
  `${resourceBankPath(subjectCode)}/awesomestuff`;

export const resourcesByActivityPath = (subjectCode: string) =>
  `${resourceBankPath(subjectCode)}/resourcesbyactivity`;

export const categoryPath = (
  subjectCode: string,
  resourceCategoriesId: number
) =>
  `${resourceBankPath(subjectCode)}/resourcecategory/${resourceCategoriesId}`;

export const activityPath = (
  subjectCode: string,
  id: number,
  bIsGroup: boolean
) =>
  `${resourceBankPath(subjectCode)}/${
    bIsGroup ? 'activitygroup' : 'activity'
  }/${id}`;

export const tilePath = (
  subjectCode: string,
  id: number,
  tileType: ResourcesTileType
) =>
  tileType === 'ResourceCategory'
    ? categoryPath(subjectCode, id)
    : activityPath(subjectCode, id, tileType === 'ActivityGroup');

export const searchPath = (subjectCode: string, searchTerm: string) =>
  `${resourceBankPath(subjectCode)}/search?searchString=${encodeURIComponent(
    searchTerm
  )}`;

export const presentationPath = (subjectCode: string, resourcesId: number) =>
  `${resourceBankPath(subjectCode)}/viewpresentation?resourceId=${resourcesId}`;

export const playerPath = (subjectCode: string, resourcesId: number) =>
  `${resourceBankPath(subjectCode)}/viewresourcefile?id=${resourcesId}`;

const ID_PATTERN = /^\d{1,9}$/;

/** A page path or query id, or null when it is not one */
export const toId = (value: unknown) =>
  typeof value === 'string' && ID_PATTERN.test(value) ? Number(value) : null;

export type ResourcesPageData<T> = {
  subject: Subject;
  labels: SubjectContents;
  details: T;
};

/**
 * A resource bank page's state: null while it loads, 'notFound' or 'error', the status's button
 * when the school's subscription does not reach the page, or the page's data
 */
export type ResourcesPageResult<T> = null | LessonsResult<ResourcesPageData<T>>;

/**
 * Loads a resource bank page's subject, labels and details. A refused call is 'notFound', unless
 * the school does not have full access and the page sends such users to their profile.
 * @param load - The page's call, or null when the path is not valid
 * @param key - Changes whenever the page asks for different details
 * @param bRefusedToProfile - False when a school without full access sees 'notFound' instead
 */
export const useResourcesPage = <T>(
  subjectCode: string | undefined,
  load: ((subjectsId: number) => Promise<LessonsResult<T>>) | null,
  key: string,
  bRefusedToProfile = true
): ResourcesPageResult<T> => {
  const [result, setResult] = useState<ResourcesPageResult<T>>(null);

  useEffect(() => {
    let bCurrent = true;
    const done = (value: ResourcesPageResult<T>) => {
      if (bCurrent) setResult(value);
    };

    setResult(null);
    if (!subjectCode || !load) {
      done('notFound');
      return;
    }

    (async () => {
      const subject = await LessonsApi.getSubjectByCode(subjectCode);
      if (typeof subject === 'string') return done(subject);

      const [details, labels] = await Promise.all([
        load(subject.subjectsID),
        LessonsApi.getSubjectContents(subject.subjectsID),
      ]);
      if (details !== 'notFound' || !bRefusedToProfile) {
        return done(
          typeof details === 'string' ? details : { subject, labels, details }
        );
      }

      const categories = await ResourcesApi.getCategories(subject.subjectsID);
      if (
        typeof categories !== 'string' &&
        categories.access.status !== 'Full'
      ) {
        if (bCurrent) navigate(AppPath.PROFILE, { replace: true });
        return;
      }
      done('notFound');
    })();

    return () => {
      bCurrent = false;
    };
    // load is a fixed api call per page, and key names what it asks for
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectCode, key]);

  return result;
};

/** Sets the browser tab's title while the page shows */
export const usePageTitle = (title: string | null) => {
  useEffect(() => {
    if (title === null) return;
    const previousTitle = document.title;
    document.title = title;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);
};

export type ResourcesRouteParams = {
  subject?: string;
  id?: string;
  type?: string;
};

export const isPageData = <T>(
  result: ResourcesPageResult<T>
): result is ResourcesPageData<T> =>
  result !== null && typeof result === 'object' && 'subject' in result;
